import {
  Component,
  OnDestroy,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  Input,
  inject,
  signal,
  computed,
  ViewEncapsulation,
  Injector,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import OlMap from 'ol/Map';
import View from 'ol/View';
import { ScaleLine, defaults as defaultControls } from 'ol/control';
import Draw, { createBox } from 'ol/interaction/Draw';
import VectorLayer from 'ol/layer/Vector';
import Layer from 'ol/layer/Layer';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { Point, LineString, Polygon, Circle, MultiPolygon } from 'ol/geom';
import {
  extend as extendExtent,
  createEmpty as createEmptyExtent,
} from 'ol/extent';
import { unByKey } from 'ol/Observable';
import { getLength } from 'ol/sphere';
import type { EventsKey } from 'ol/events';
import Overlay from 'ol/Overlay';
import { Fill, Stroke, Style } from 'ol/style';
import type { StyleLike } from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import type { Type as GeometryType } from 'ol/geom/Geometry';
import type Feature from 'ol/Feature';
import OlFeature from 'ol/Feature';
import { getRenderPixel } from 'ol/render';
import type RenderEvent from 'ol/render/Event';

import type BaseLayer from 'ol/layer/Base';

import { CatalogueApiService, OGC_TILES_BASE_URL } from '@angular-workspace/services';
import { CANVAS_ICONS_BASE_URL } from '../../canvas-icons.constants';
import { CANVAS_COLOR_VARS, getCanvasColor } from '../../canvas-theme.utils';
import {
  CANVAS_BOUNDARY_GEOJSON_URL,
  CANVAS_MAP_EXTENT,
  CANVAS_DATASET_DETAIL_URL,
  CANVAS_NOTIFIER,
  OGC_TILES_COLLECTION_ID,
} from '../../../canvas-tokens';
import ImageLayer from 'ol/layer/Image';
import {
  exportLayerFeaturesAsGeoJson,
  createStacImageLayer,
  createBboxBoundaryLayer,
  createDatasetVectorStyleFn,
  createVectorTileLayerFromUrl,
  createRasterTileLayerFromUrl,
  fitMapToExtent,
  type StacBbox,
  type PlottedOlLayerBundle,
} from '../canvas-map-ol.utils';
import {
  getRandomLayerColor,
  markerIconStyle,
  vectorColorStyle,
  measurementDrawStyle,
  formatMeasureLength,
} from '../layers/canvas-layer-style.utils';
import {
  CanvasStacListModalComponent,
  type CanvasStacPlotPayload,
} from '../stac/canvas-stac-list-modal.component';
import {
  CanvasOgcFeaturesApiService,
  type CanvasOgcFeature,
  type CanvasBboxSearchHit,
} from '../ogc/canvas-ogc-features-api.service';
import { geometryToOgcBbox } from '../bbox/canvas-bbox.utils';
import { CanvasDatasetsService } from '../../canvas-datasets.service';
import {
  fetchPublishedDatasetIds,
  type CanvasDataset,
} from '../../canvas-catalogue.util';
import { CanvasToolbarComponent } from '../toolbar/toolbar-home.component';
import { CanvasSearchMode } from '../toolbar/tools/search-tool.component';
import { DrawShapeMode } from '../toolbar/tools/draw-polygon-tool.component';
import type { ImportedFeaturesPayload } from '../toolbar/tools/import-tool.component';
import { DatasetsModalComponent } from '../datasets/datasets-modal.component';
import { CanvasBboxResultsModalComponent } from '../bbox/canvas-bbox-results-modal.component';
import { CanvasBboxSearchBannerComponent } from '../bbox/canvas-bbox-search-banner.component';
import { CanvasLayersToggleComponent } from '../layers/canvas-layers-toggle.component';
import {
  CanvasLayersPanelComponent,
  type LayerNameChangePayload,
} from '../layers/canvas-layers-panel.component';
import { CanvasDatasetLoadService } from '../dataset-load/canvas-dataset-load.service';
import type { DatasetQueryParams } from '../dataset-load/canvas-dataset-load.utils';
import { buildDatasetQueryParams } from '../dataset-load/canvas-dataset-load.utils';
import { CanvasAttrFilterModalComponent } from '../attr-filter/canvas-attr-filter-modal.component';
import { KeycloakService } from 'keycloak-angular';
import type {
  CanvasExportLayerDescriptor,
  CanvasLayerPanelEntry,
  CanvasLayerStyleControls,
  UserDrawLayerRowState,
  UserDrawShapeKind,
  Basemap,
  BasemapType,
} from '../canvas-maps.models';
import {
  CV_BASEMAP_KEY,
  MAHARASHTRA_MAP_EXTENT,
  createStandardOsmLayer,
  createHumanitarianBasemapLayer,
  createTerrainBasemapLayer,
  createOpenSeriesBasemapLayer,
  createBasicLightBasemapLayer,
  createBasicDarkBasemapLayer,
  createMaharashtraBoundaryLayer,
  createMaharashtraOutsideDimLayer,
} from '../canvas-map-basemap.utils';

/**
 * Pin (+ marker) scale vs view zoom: smaller when zoomed out, larger when zoomed in.
 * Uses a smoothstep so the change feels gradual between country and street scale.
 */
function mapMarkerIconScaleFromZoom(zoom: number): number {
  const minZ = 2;
  const maxZ = 20;
  const minScale = 0.5;
  const maxScale = 3.25;
  const z = Math.min(maxZ, Math.max(minZ, zoom));
  const t = (z - minZ) / (maxZ - minZ);
  const smooth = t * t * (3 - 2 * t);
  return minScale + smooth * (maxScale - minScale);
}

/** Toolbar gallery entries — mirrors ui-canvas `BaseMaps` list order when OGC tiles are configured. */
function canvasToolbarBasemapEntries(
  iconsBaseUrl: string,
  ogcBaseUrl?: string | null,
  collectionId?: string | null,
): Basemap[] {
  const icons = `${iconsBaseUrl.replace(/\/+$/, '')}/basemaps`;
  const base = (ogcBaseUrl ?? '').trim();
  const cid = (collectionId ?? '').trim();
  const thumb = {
    osm: `${icons}/osm.svg`,
    openSeries: `${icons}/openseries.svg`,
    humanitarian: `${icons}/humanitarian.svg`,
    terrain: `${icons}/terrain.svg`,
    basicLight: `${icons}/ogc_light.svg`,
    basicDark: `${icons}/ogc_dark.svg`,
  } as const;
  const tail: Basemap[] = [
    {
      type: 'humanitarian',
      name: 'Humanitarian',
      previewUrl: thumb.humanitarian,
    },
    { type: 'terrain', name: 'Terrain', previewUrl: thumb.terrain },
    { type: 'basiclight', name: 'Basic light', previewUrl: thumb.basicLight },
    { type: 'basicdark', name: 'Basic dark', previewUrl: thumb.basicDark },
  ];
  if (base && cid) {
    return [
      { type: 'osm', name: 'OSM', previewUrl: thumb.osm },
      {
        type: 'openseriesmap',
        name: 'Open series',
        previewUrl: thumb.openSeries,
      },
      ...tail,
    ];
  }
  return [
    { type: 'osm', name: 'OSM', previewUrl: thumb.osm },
    {
      type: 'openseriesmap',
      name: 'Open series',
      previewUrl: thumb.openSeries,
    },
    ...tail,
  ];
}

@Component({
  selector: 'lib-maps-home',
  standalone: true,
  imports: [
    CommonModule,
    CanvasToolbarComponent,
    DatasetsModalComponent,
    CanvasBboxResultsModalComponent,
    CanvasBboxSearchBannerComponent,
    CanvasLayersToggleComponent,
    CanvasLayersPanelComponent,
    CanvasAttrFilterModalComponent,
    CanvasStacListModalComponent,
  ],
  templateUrl: './maps-home.component.html',
  styleUrl: './maps-home.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class MapsHomeComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() mapVisible = true;

  readonly iconsBaseUrl = inject(CANVAS_ICONS_BASE_URL);
  private readonly boundaryGeoJsonUrl = inject(CANVAS_BOUNDARY_GEOJSON_URL, { optional: true });
  private readonly mapExtent = inject(CANVAS_MAP_EXTENT, { optional: true }) ?? MAHARASHTRA_MAP_EXTENT;
  private readonly injector = inject(Injector);
  private readonly ogcTilesBaseUrl = inject(OGC_TILES_BASE_URL, {
    optional: true,
  }) as string | undefined;
  private readonly ogcTilesCollectionId = inject(OGC_TILES_COLLECTION_ID, {
    optional: true,
  }) as string | undefined;
  private readonly ogcFeaturesApi = inject(CanvasOgcFeaturesApiService);
  private readonly catalogueApi = inject(CatalogueApiService);
  private readonly datasetLoadService = inject(CanvasDatasetLoadService);
  private readonly keycloak = inject(KeycloakService);
  readonly datasetsService = inject(CanvasDatasetsService);
  private readonly notifier = inject(CANVAS_NOTIFIER, { optional: true });
  private readonly datasetDetailUrl =
    inject(CANVAS_DATASET_DETAIL_URL, { optional: true }) ??
    ((id: string) => `/dataset/${id}`);

  /** Base-layer picker entries (ui-canvas style gallery). */
  readonly basemapOptions: Basemap[] = canvasToolbarBasemapEntries(
    this.iconsBaseUrl,
    this.ogcTilesBaseUrl,
    this.ogcTilesCollectionId,
  );
  readonly activeBasemap = signal<BasemapType>('osm');
  /** True while the base map is being switched/rendered. */
  readonly basemapLoading = signal(false);

  @ViewChild('mapContainer') mapContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('layerExportAnchor')
  private layerExportAnchor?: ElementRef<HTMLAnchorElement>;

  readonly userDrawSource = new VectorSource();
  private readonly bboxDrawSource = new VectorSource();
  /** Ephemeral markers shown after a bbox catalogue search. */
  private readonly bboxSearchSource = new VectorSource();
  private bboxSearchLayer!: VectorLayer<VectorSource>;
  private bboxOverlayLayer!: VectorLayer<VectorSource>;
  /** One OpenLayers layer per plotted dataset (ui-canvas addCanvasLayer parity). */
  private readonly plottedDatasetLayers = new Map<
    string,
    { source?: VectorSource; olLayer: BaseLayer; isImageLayer?: boolean }
  >();
  private bboxMode: 'search' | 'filter' | null = null;
  private bboxFilterDataset: CanvasDataset | null = null;
  private bboxNoticeTimer: ReturnType<typeof setTimeout> | null = null;
  readonly isLayerPanelOpen = signal(false);
  readonly layerExportDialogOpen = signal(false);
  readonly layerExportEntryId = signal<string | null>(null);
  layerExportFileName = '';
  /** User drawings and plotted datasets listed in the layers panel. */
  readonly drawnLayerEntries = signal<CanvasLayerPanelEntry[]>([]);
  private datasetLayerOrdinal = 0;
  /** Base z-index for layers-panel entries (ui-canvas `swapLayerPosition`). */
  private static readonly PANEL_LAYER_Z_BASE = 81;
  private shapeOrdinal: Record<UserDrawShapeKind, number> = {
    circle: 0,
    rectangle: 0,
    polygon: 0,
    marker: 0,
  };
  readonly isDatasetsModalOpen = signal(false);
  /** True while the user is drawing a search bbox on the map. */
  readonly bboxSearchActive = signal(false);
  readonly bboxSearchLoading = signal(false);
  readonly bboxSearchHits = signal<CanvasBboxSearchHit[]>([]);
  readonly bboxSearchSummaryVisible = signal(false);
  readonly isBboxResultsModalOpen = signal(false);
  readonly selectedBboxResourceIds = signal<Set<string>>(new Set());
  readonly bboxSelectedCount = computed(
    () => this.selectedBboxResourceIds().size,
  );
  /** True while fetching dataset features (Get All / bbox filter / attr filter). */
  readonly datasetLoadLoading = signal(false);
  /** Dataset for which the attribute filter dialog is open. */
  readonly attrFilterDataset = signal<CanvasDataset | null>(null);
  readonly isAttrFilterModalOpen = signal(false);
  readonly isStacModalOpen = signal(false);
  readonly stacDataset = signal<CanvasDataset | null>(null);
  /** Feature properties sidebar — opened when the user clicks a loaded OGC feature. */
  readonly isSidebarOpen = signal(false);
  readonly sidebarProperties = signal<Record<string, unknown> | null>(null);
  /** Layers-panel entry id for the feature currently shown in the sidebar. */
  readonly sidebarEntryId = signal<string | null>(null);

  /** Ephemeral message when swipe is used with no drawn layers. */
  readonly swipeEmptyNotice = signal<string | null>(null);
  /** Hint while bbox search draw mode is active or after fetch. */
  readonly bboxSearchNotice = signal<string | null>(null);
  private swipeEmptyNoticeTimer: ReturnType<typeof setTimeout> | null = null;

  /** Shows the layer-picker setup panel. */
  readonly swipeDialogOpen = signal(false);
  /** Whether the swipe comparison is actively running. */
  readonly swipeActive = signal(false);
  /** Fraction (0–1) of map width where the swipe divider sits. */
  readonly swipePosition = signal(0.5);
  /** Layer IDs shown on the left side (multi-select). */
  readonly swipeLeftIds = signal<string[]>([]);
  /** Layer IDs shown on the right side (multi-select). */
  readonly swipeRightIds = signal<string[]>([]);
  readonly zoomLevel = signal(4.5);
  /** True while waiting for a map click to place a point (add-marker tool). */
  readonly addPointToolActive = signal(false);
  /** Which high-level tool group is active. Drives the select-tool active state. */
  readonly activeToolGroup = signal<'select' | 'draw' | 'point' | 'measure'>(
    'select',
  );
  /** True when no drawing interaction is active (select tool highlighted). */
  readonly selectToolActive = computed(
    () => this.activeToolGroup() === 'select',
  );
  readonly measureToolActive = computed(
    () => this.activeToolGroup() === 'measure',
  );

  /** Set after map init; toolbar binds when present. */
  map: OlMap | null = null;

  get zoomPercent(): number {
    return Math.round((this.zoomLevel() / 18) * 100);
  }

  private baseLayer!: BaseLayer;
  private userFeaturesLayer!: VectorLayer<VectorSource>;
  private pointMarkerIcon!: Icon;
  private pointMarkerStyle!: Style;
  private activeDraw: Draw | null = null;
  private measureOverlays: Overlay[] = [];
  private measureTooltipOverlay: Overlay | null = null;
  private measureTooltipElement: HTMLElement | null = null;
  private measureGeometryListener: EventsKey | null = null;
  /** prerender/postrender clip handlers attached during swipe compare. */
  private readonly swipeClipBindings = new Map<
    Layer,
    { prerender: (e: RenderEvent) => void; postrender: (e: RenderEvent) => void }
  >();
  private swipeLeftVLayer: VectorLayer<VectorSource> | null = null;
  private swipeRightVLayer: VectorLayer<VectorSource> | null = null;
  /** Draw-layer ids shown on both sides (no clip) while swipe is active. */
  private swipeMiddleDrawIds: string[] = [];
  /** Cached async-loaded boundary base maps. */
  private basicLightBasemap: Awaited<
    ReturnType<typeof createBasicLightBasemapLayer>
  > | null = null;
  private basicDarkBasemap: Awaited<
    ReturnType<typeof createBasicDarkBasemapLayer>
  > | null = null;
  private maharashtraBoundaryLayer: Awaited<
    ReturnType<typeof createMaharashtraBoundaryLayer>
  > | null = null;
  private maharashtraDimLayer: Awaited<
    ReturnType<typeof createMaharashtraOutsideDimLayer>
  > | null = null;
  private cachedOsm: ReturnType<typeof createStandardOsmLayer> | null = null;
  private cachedHumanitarian: ReturnType<
    typeof createHumanitarianBasemapLayer
  > | null = null;
  private cachedTerrain: ReturnType<typeof createTerrainBasemapLayer> | null =
    null;
  private cachedOpenSeries: BaseLayer | null = null;
  /** Bound reference kept so we can remove it on destroy. */
  private readonly onMapContextMenu = (e: MouseEvent): void => {
    if (this.activeToolGroup() === 'select') return;
    e.preventDefault();
    this.onSelectTool();
  };

  private readonly onViewResolutionChange = (): void => {
    if (!this.map) return;
    this.zoomLevel.set(this.map.getView().getZoom() ?? 4.5);
    this.syncPointMarkerScale();
  };

  ngOnChanges(changes: SimpleChanges): void {
    const vis = changes['mapVisible'];
    if (vis && !vis.firstChange && this.mapVisible && this.map) {
      setTimeout(() => this.map!.updateSize(), 50);
    }
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  private initMap(): void {
    const scaleControl = new ScaleLine({ units: 'metric', minWidth: 80 });
    this.cachedOsm = createStandardOsmLayer();
    this.baseLayer = this.cachedOsm;

    const markerSrc = `${this.iconsBaseUrl.replace(/\/$/, '')}/add-location-map.svg`;
    this.pointMarkerIcon = new Icon({
      src: markerSrc,
      anchor: [0.5, 1],
      anchorXUnits: 'fraction',
      anchorYUnits: 'fraction',
      scale: mapMarkerIconScaleFromZoom(4.5),
    });
    this.pointMarkerStyle = new Style({ image: this.pointMarkerIcon });

    const bboxStroke = new Stroke({ color: getCanvasColor(CANVAS_COLOR_VARS.bboxStroke), width: 2 });
    const bboxFill = new Fill({ color: getCanvasColor(CANVAS_COLOR_VARS.bboxFill) });
    const ogcStroke = new Stroke({ color: getCanvasColor(CANVAS_COLOR_VARS.ogcStroke), width: 2 });
    const ogcFill = new Fill({ color: getCanvasColor(CANVAS_COLOR_VARS.ogcFill) });

    this.bboxOverlayLayer = new VectorLayer({
      source: this.bboxDrawSource,
      zIndex: 85,
      style: new Style({ stroke: bboxStroke, fill: bboxFill }),
    });

    const bboxSelectedStroke = new Stroke({ color: getCanvasColor(CANVAS_COLOR_VARS.bboxStroke), width: 3 });
    this.bboxSearchLayer = new VectorLayer({
      source: this.bboxSearchSource,
      zIndex: 90,
      style: (feat) => {
        const feature = feat as Feature;
        const g = feature.getGeometry();
        if (!g) return undefined;
        if (g instanceof Point) {
          if (feature.get('cvBboxSelected')) {
            return new Style({
              image: this.pointMarkerIcon,
              stroke: bboxSelectedStroke,
            });
          }
          return this.pointMarkerStyle;
        }
        return new Style({ stroke: ogcStroke, fill: ogcFill });
      },
    });

    this.userFeaturesLayer = new VectorLayer({
      source: this.userDrawSource,
      zIndex: 80,
      style: (feat) => this.styleUserDrawFeature(feat as Feature),
    });

    const olMap = new OlMap({
      target: this.mapContainerRef.nativeElement,
      view: new View({
        center: [
          (this.mapExtent[0] + this.mapExtent[2]) / 2,
          (this.mapExtent[1] + this.mapExtent[3]) / 2,
        ],
        projection: 'EPSG:4326',
        zoom: 6.8,
      }),
      controls: defaultControls({ zoom: false, attribution: false }).extend([
        scaleControl,
      ]),
      layers: [
        this.baseLayer,
        this.userFeaturesLayer,
        this.bboxOverlayLayer,
        this.bboxSearchLayer,
      ],
    });

    this.map = olMap;

    olMap.getView().on('change:resolution', this.onViewResolutionChange);
    this.onViewResolutionChange();

    // Right-click on the map cancels the active draw / measure tool.
    olMap.getViewport().addEventListener('contextmenu', this.onMapContextMenu);

    olMap.on('click', (evt) => this.onMapFeatureClick(evt));

    void this.loadMaharashtraBoundaries();
  }

  private onMapFeatureClick(evt: { pixel: number[] }): void {
    if (!this.map) return;

    const feature = this.map.forEachFeatureAtPixel(
      evt.pixel,
      (f, layer) => {
        if (layer === this.bboxSearchLayer) return f;
        for (const { olLayer } of this.plottedDatasetLayers.values()) {
          if (layer === olLayer) return f;
        }
        return undefined;
      },
      { hitTolerance: 8 },
    ) as Feature | undefined;

    if (!feature) {
      this.closeSidebar();
      return;
    }

    // Bbox search mode: toggle resource selection as before.
    if (this.bboxSearchSummaryVisible()) {
      const hit = feature.get('cvBboxSearchHit') as
        | CanvasBboxSearchHit
        | undefined;
      if (hit?.resourceId) {
        this.onToggleBboxResourceSelection(hit.resourceId);
        return;
      }
    }

    // OGC dataset feature: show property sidebar.
    if (feature.get('cvOgcFeature')) {
      const entryId = feature.get('cvEntryId') as string | undefined;
      if (entryId && entryId !== this.sidebarEntryId()) {
        this.closeSidebar();
      }
      const raw = feature.getProperties() as Record<string, unknown>;
      const SKIP = new Set([
        'geometry',
        'cvOgcFeature',
        'cvLayerName',
        'cvBboxSearchHit',
        'cvBboxSelected',
        'cvEntryId',
      ]);
      const props: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(raw)) {
        if (!SKIP.has(k)) {
          props[k] = v;
        }
      }
      this.sidebarEntryId.set(entryId ?? null);
      this.sidebarProperties.set(props);
      this.isSidebarOpen.set(true);
      return;
    }

    this.closeSidebar();
  }

  closeSidebar(): void {
    this.isSidebarOpen.set(false);
    this.sidebarProperties.set(null);
    this.sidebarEntryId.set(null);
  }

  /** Close feature sidebar when the user selects a different layer in the panel. */
  onLayerSelect(layerId: string): void {
    const activeEntryId = this.sidebarEntryId();
    if (this.isSidebarOpen() && activeEntryId && activeEntryId !== layerId) {
      this.closeSidebar();
    }
  }

  /** Returns Object.entries of an object — used in the template. */
  objectEntries(obj: Record<string, unknown>): [string, unknown][] {
    return Object.entries(obj)
      .filter(([key]) => !key.startsWith('cv'))
      .sort(([a], [b]) => a.localeCompare(b));
  }

  formatPropValue(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  private async loadMaharashtraBoundaries(): Promise<void> {
    if (!this.map) return;
    if (!this.boundaryGeoJsonUrl) {
      console.warn(
        '[Canvas] CANVAS_BOUNDARY_GEOJSON_URL is not provided. ' +
          'Boundary overlay and basic-light/dark basemaps will not render. ' +
          'Add `{ provide: CANVAS_BOUNDARY_GEOJSON_URL, useValue: \'/assets/...\' }` to your app providers.',
      );
      return;
    }
    try {
      const layers = this.map.getLayers();
      const dimLayer = (this.maharashtraDimLayer ??=
        await createMaharashtraOutsideDimLayer(this.boundaryGeoJsonUrl));
      dimLayer.setZIndex(8);
      if (!layers.getArray().includes(dimLayer)) {
        layers.insertAt(1, dimLayer);
      }

      const layer = (this.maharashtraBoundaryLayer ??=
        await createMaharashtraBoundaryLayer(this.boundaryGeoJsonUrl));
      layer.setZIndex(15);
      if (!layers.getArray().includes(layer)) {
        layers.insertAt(2, layer);
      }
      this.syncMaharashtraOverlayVisibility(this.activeBasemap());
      const sourceExtent = layer.getSource()?.getExtent();
      const extent =
        sourceExtent && Number.isFinite(sourceExtent[0])
          ? sourceExtent
          : this.mapExtent;
      this.map.getView().fit(extent, {
        padding: [48, 48, 48, 48],
        maxZoom: 8,
        duration: 350,
      });
      this.onViewResolutionChange();
    } catch (err) {
      console.error('Boundary layer failed to load', err);
    }
  }

  /** Division polygons are the basemap for basic light/dark — hide duplicate overlays. */
  private syncMaharashtraOverlayVisibility(basemap: BasemapType): void {
    const hide = basemap === 'basiclight' || basemap === 'basicdark';
    this.maharashtraBoundaryLayer?.setVisible(!hide);
    this.maharashtraDimLayer?.setVisible(!hide);
  }

  onBasemapChange(type: BasemapType): void {
    if (this.activeBasemap() === type || this.basemapLoading()) return;
    void this.applyBasemap(type);
  }

  private removeBasemapLayers(): void {
    if (!this.map) return;
    const layers = this.map.getLayers();
    const toRemove: BaseLayer[] = [];
    layers.forEach((lyr) => {
      if (lyr.get(CV_BASEMAP_KEY)) {
        toRemove.push(lyr);
      }
    });
    toRemove.forEach((lyr) => this.map!.removeLayer(lyr));
  }

  private getRasterBasemap(type: BasemapType): BaseLayer {
    switch (type) {
      case 'osm':
        return (this.cachedOsm ??= createStandardOsmLayer());
      case 'humanitarian':
        return (this.cachedHumanitarian ??= createHumanitarianBasemapLayer());
      case 'terrain':
        return (this.cachedTerrain ??= createTerrainBasemapLayer());
      case 'openseriesmap': {
        const base = (this.ogcTilesBaseUrl ?? '').trim();
        const cid = (this.ogcTilesCollectionId ?? '').trim();
        if (!base || !cid) {
          return this.getRasterBasemap('osm');
        }
        return (this.cachedOpenSeries ??= createOpenSeriesBasemapLayer(
          base,
          cid,
        ));
      }
      default:
        return this.getRasterBasemap('osm');
    }
  }

  private async ensureBasemapLayer(type: BasemapType): Promise<BaseLayer> {
    if (type === 'basiclight' || type === 'basicdark') {
      if (!this.boundaryGeoJsonUrl) {
        console.warn(
          `[Canvas] Cannot load "${type}" basemap: CANVAS_BOUNDARY_GEOJSON_URL is not provided. ` +
            'Falling back to OSM.',
        );
        return this.getRasterBasemap('osm');
      }
      if (type === 'basiclight') {
        return (this.basicLightBasemap ??= await createBasicLightBasemapLayer(
          this.boundaryGeoJsonUrl,
        ));
      }
      return (this.basicDarkBasemap ??= await createBasicDarkBasemapLayer(
        this.boundaryGeoJsonUrl,
      ));
    }
    return this.getRasterBasemap(type);
  }

  private async applyBasemap(type: BasemapType): Promise<void> {
    if (!this.map) return;
    this.basemapLoading.set(true);
    try {
      const layer = await this.ensureBasemapLayer(type);
      this.removeBasemapLayers();
      this.map.getLayers().insertAt(0, layer);
      this.activeBasemap.set(type);
      this.syncMaharashtraOverlayVisibility(type);
      this.map.render();
    } catch (err) {
      console.error('Basemap switch failed', err);
    } finally {
      // Small delay prevents a flash when switching to a cached layer.
      setTimeout(() => this.basemapLoading.set(false), 180);
    }
  }

  private syncPointMarkerScale(): void {
    if (!this.map || !this.pointMarkerIcon || !this.userFeaturesLayer) return;
    const z = this.map.getView().getZoom() ?? 4.5;
    this.pointMarkerIcon.setScale(mapMarkerIconScaleFromZoom(z));
    this.userFeaturesLayer.changed();
  }

  clearInteractions(options?: { keepBboxSearch?: boolean }): void {
    if (!this.map) return;
    if (this.activeDraw) {
      this.map.removeInteraction(this.activeDraw);
      this.activeDraw = null;
    }
    for (const o of this.measureOverlays) {
      this.map.removeOverlay(o);
    }
    this.measureOverlays = [];
    this.disposeMeasureTooltip();
    this.addPointToolActive.set(false);
    if (this.bboxSearchActive() && !options?.keepBboxSearch) {
      this.cancelBboxSearch();
    }
  }

  onSelectTool(): void {
    this.clearInteractions();
    this.activeToolGroup.set('select');
  }

  onLocate(): void {
    if (!this.map || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.map!.getView().animate({
          center: [pos.coords.longitude, pos.coords.latitude],
          zoom: Math.max(this.map!.getView().getZoom() ?? 4, 12),
          duration: 450,
        });
      },
      () => undefined,
      { enableHighAccuracy: true, timeout: 12_000 },
    );
  }

  onDrawShape(mode: DrawShapeMode): void {
    if (!this.map) return;
    this.clearInteractions();
    this.activeToolGroup.set('draw');

    let type: GeometryType = 'Polygon';
    let geometryFunction: ReturnType<typeof createBox> | undefined;
    if (mode === 'rectangle') {
      type = 'Circle';
      geometryFunction = createBox();
    } else if (mode === 'circle') {
      type = 'Circle';
    } else {
      type = 'Polygon';
    }

    const drawOpts: {
      source: VectorSource;
      type: GeometryType;
      stopClick: boolean;
      geometryFunction?: ReturnType<typeof createBox>;
    } = {
      source: this.userDrawSource,
      type,
      stopClick: true,
    };
    if (geometryFunction) {
      drawOpts.geometryFunction = geometryFunction;
    }
    const draw = new Draw(drawOpts);
    draw.on('drawend', (e) => {
      e.feature.set('cvShape', mode);
      this.registerDrawnShapeLayer(e.feature as Feature, mode);
      this.activeToolGroup.set('select');
    });
    this.map.addInteraction(draw);
    this.activeDraw = draw;
  }

  onAddPoint(): void {
    if (!this.map) return;
    this.clearInteractions();
    this.activeToolGroup.set('point');
    const draw = new Draw({
      source: this.userDrawSource,
      type: 'Point',
      stopClick: true,
      style: markerIconStyle(this.iconsBaseUrl, 0),
    });
    draw.on('drawend', (e) => {
      e.feature.set('cvShape', 'point');
      this.registerUserLayer(e.feature as Feature, 'marker');
      this.map!.removeInteraction(draw);
      if (this.activeDraw === draw) {
        this.activeDraw = null;
      }
      this.addPointToolActive.set(false);
      this.activeToolGroup.set('select');
    });
    this.map.addInteraction(draw);
    this.activeDraw = draw;
    this.addPointToolActive.set(true);
  }

  onMeasure(): void {
    if (!this.map) return;
    this.clearInteractions();
    this.activeToolGroup.set('measure');
    const projection = this.map.getView().getProjection();
    if (!projection) return;

    const draw = new Draw({
      type: 'LineString',
      stopClick: true,
      style: measurementDrawStyle(),
    });

    draw.on('drawstart', (evt) => {
      this.disposeMeasureTooltip();
      const tip = document.createElement('div');
      tip.className = 'cv-measure-tip';
      const overlay = new Overlay({
        element: tip,
        positioning: 'bottom-center',
        offset: [0, -8],
        stopEvent: false,
      });
      this.measureTooltipElement = tip;
      this.measureTooltipOverlay = overlay;
      this.map!.addOverlay(overlay);

      const feature = evt.feature;
      const geometry = feature.getGeometry();
      if (!geometry) return;

      this.measureGeometryListener = geometry.on('change', (event) => {
        const geom = event.target;
        if (!(geom instanceof LineString)) return;
        const length = getLength(geom, { projection });
        if (length <= 0) return;
        tip.textContent = formatMeasureLength(geom, projection);
        overlay.setPosition(geom.getLastCoordinate());
      });
    });

    const finishMeasure = (): void => {
      if (this.measureGeometryListener) {
        unByKey(this.measureGeometryListener);
        this.measureGeometryListener = null;
      }
      this.disposeMeasureTooltip();
      this.map?.removeInteraction(draw);
      this.activeDraw = null;
      this.activeToolGroup.set('select');
    };

    draw.on('drawend', finishMeasure);
    draw.on('drawabort', finishMeasure);

    this.map.addInteraction(draw);
    this.activeDraw = draw;
  }

  private disposeMeasureTooltip(): void {
    if (this.measureGeometryListener) {
      unByKey(this.measureGeometryListener);
      this.measureGeometryListener = null;
    }
    if (this.measureTooltipOverlay) {
      this.map?.removeOverlay(this.measureTooltipOverlay);
      this.measureTooltipOverlay = null;
    }
    this.measureTooltipElement?.remove();
    this.measureTooltipElement = null;
  }

  toggleLayerPanel(): void {
    this.isLayerPanelOpen.update((v) => !v);
  }

  onLayerColorChange(entryId: string, color: string): void {
    this.drawnLayerEntries.update((arr) =>
      arr.map((e) => (e.id === entryId ? { ...e, layerColor: color } : e)),
    );
    this.applyLayerColorOnMap(entryId, color);
  }

  onLayerMarkerChange(entryId: string, iconId: number): void {
    this.drawnLayerEntries.update((arr) =>
      arr.map((e) =>
        e.id === entryId ? { ...e, markerIconId: iconId } : e,
      ),
    );
    this.applyLayerMarkerOnMap(entryId, iconId);
  }

  onReorderLayers(orderedIds: string[]): void {
    const byId = new Map(this.drawnLayerEntries().map((e) => [e.id, e]));
    const next: CanvasLayerPanelEntry[] = [];
    for (const id of orderedIds) {
      const entry = byId.get(id);
      if (entry) {
        next.push(entry);
        byId.delete(id);
      }
    }
    for (const entry of byId.values()) {
      next.push(entry);
    }
    this.drawnLayerEntries.set(next);
    this.applyLayerStackOrder(next);
  }

  /** Apply panel order to map layer z-index (ui-canvas `swapLayerPosition`). */
  private applyLayerStackOrder(entries: CanvasLayerPanelEntry[]): void {
    if (!this.map) return;

    const drawIndices = entries
      .map((entry, index) => (entry.kind === 'draw' ? index : -1))
      .filter((index) => index >= 0);

    if (drawIndices.length > 0) {
      this.userFeaturesLayer.setZIndex(
        MapsHomeComponent.PANEL_LAYER_Z_BASE + Math.min(...drawIndices),
      );
      this.reorderUserDrawFeatures(entries);
    }

    entries.forEach((entry, index) => {
      if (entry.kind !== 'dataset') return;
      const bundle = this.plottedDatasetLayers.get(entry.id);
      bundle?.olLayer.setZIndex(MapsHomeComponent.PANEL_LAYER_Z_BASE + index);
    });
  }

  private reorderUserDrawFeatures(entries: CanvasLayerPanelEntry[]): void {
    const drawIds = entries
      .filter((entry) => entry.kind === 'draw')
      .map((entry) => entry.id);
    if (!drawIds.length) return;

    const drawIdSet = new Set(drawIds);
    const featureById = new Map<string, Feature>();
    const trailing: Feature[] = [];

    for (const feature of this.userDrawSource.getFeatures()) {
      const entryId = feature.get('cvEntryId') as string | undefined;
      if (entryId && drawIdSet.has(entryId)) {
        featureById.set(entryId, feature);
      } else {
        trailing.push(feature);
      }
    }

    const ordered = drawIds
      .map((id) => featureById.get(id))
      .filter((feature): feature is Feature => !!feature);
    if (!ordered.length) return;

    this.userDrawSource.clear();
    this.userDrawSource.addFeatures([...ordered, ...trailing]);
    this.userFeaturesLayer.changed();
  }

  onLayerOpacityChange(entryId: string, opacity: number): void {
    const clamped = Math.min(1, Math.max(0, opacity));
    this.drawnLayerEntries.update((arr) =>
      arr.map((e) =>
        e.id === entryId ? { ...e, layerOpacity: clamped } : e,
      ),
    );
    const plotted = this.plottedDatasetLayers.get(entryId);
    if (plotted?.olLayer && typeof plotted.olLayer.setOpacity === 'function') {
      plotted.olLayer.setOpacity(clamped);
    }
  }

  private applyLayerColorOnMap(entryId: string, color: string): void {
    const plotted = this.plottedDatasetLayers.get(entryId);
    if (plotted?.source) {
      for (const f of plotted.source.getFeatures()) {
        f.set('cvLayerColor', color);
      }
      plotted.olLayer.changed();
      return;
    }
    for (const f of this.userDrawSource.getFeatures()) {
      if (f.get('cvEntryId') === entryId) {
        f.set('cvLayerColor', color);
      }
    }
    this.userFeaturesLayer.changed();
  }

  private applyLayerMarkerOnMap(entryId: string, iconId: number): void {
    const plotted = this.plottedDatasetLayers.get(entryId);
    if (plotted?.source) {
      for (const f of plotted.source.getFeatures()) {
        f.set('cvMarkerIconId', iconId);
      }
      plotted.olLayer.changed();
      return;
    }
    for (const f of this.userDrawSource.getFeatures()) {
      if (f.get('cvEntryId') === entryId) {
        f.set('cvMarkerIconId', iconId);
      }
    }
    this.userFeaturesLayer.changed();
  }

  private drawStyleControls(shape: UserDrawShapeKind): CanvasLayerStyleControls {
    if (shape === 'marker') {
      return { color: false, marker: true, opacity: false };
    }
    return { color: true, marker: false, opacity: false };
  }

  private datasetStyleControls(options: {
    hasPoint: boolean;
    hasArea: boolean;
    isImage: boolean;
  }): CanvasLayerStyleControls {
    return {
      color: options.hasArea && !options.isImage,
      marker: options.hasPoint,
      opacity: options.isImage,
    };
  }

  private buildDrawLayerEntry(
    id: string,
    shape: UserDrawShapeKind,
    defaultLabel: string,
    rowState: UserDrawLayerRowState,
  ): CanvasLayerPanelEntry {
    const layerColor =
      shape === 'marker' ? getCanvasColor(CANVAS_COLOR_VARS.markerDefault) : getRandomLayerColor();
    return {
      id,
      kind: 'draw',
      shape,
      defaultLabel,
      name: defaultLabel,
      visible: true,
      rowState,
      layerColor,
      markerIconId: 0,
      layerOpacity: 1,
      styleControls: this.drawStyleControls(shape),
    };
  }

  private buildDatasetLayerEntry(
    id: string,
    datasetId: string,
    defaultLabel: string,
    styleControls: CanvasLayerStyleControls,
    layerColor = getCanvasColor(CANVAS_COLOR_VARS.layerDefault),
  ): CanvasLayerPanelEntry {
    return {
      id,
      kind: 'dataset',
      shape: 'dataset',
      datasetId,
      defaultLabel,
      name: defaultLabel,
      visible: true,
      rowState: 'saved',
      layerColor,
      markerIconId: 0,
      layerOpacity: 1,
      styleControls,
    };
  }

  onToggleDrawnLayer(entryId: string): void {
    const current = this.drawnLayerEntries().find((e) => e.id === entryId);
    if (!current) return;
    const nextVisible = !current.visible;
    this.drawnLayerEntries.update((arr) =>
      arr.map((e) => (e.id === entryId ? { ...e, visible: nextVisible } : e)),
    );
    if (current.kind === 'dataset') {
      this.plottedDatasetLayers.get(entryId)?.olLayer.setVisible(nextVisible);
      return;
    }
    for (const f of this.userDrawSource.getFeatures()) {
      if (f.get('cvEntryId') === entryId) {
        f.set('cvHidden', !nextVisible);
        break;
      }
    }
    this.userFeaturesLayer.changed();
  }

  onDrawnLayerNameChange(payload: LayerNameChangePayload): void {
    this.drawnLayerEntries.update((arr) =>
      arr.map((e) => (e.id === payload.id ? { ...e, name: payload.name } : e)),
    );
  }

  onSaveDrawnLayerName(entryId: string): void {
    const current = this.drawnLayerEntries().find((e) => e.id === entryId);
    if (!current) return;
    const resolved = current.name.trim() || current.defaultLabel;
    this.drawnLayerEntries.update((arr) =>
      arr.map((e) =>
        e.id === entryId
          ? { ...e, name: resolved, rowState: 'saved' as const }
          : e,
      ),
    );
    this.syncLayerNameOnFeatures(entryId, resolved);
  }

  /** Revert label to default, disable editing (reset / "remove" name flow). Keeps the shape on the map. */
  onResetDrawnLayerToDefault(entryId: string): void {
    const current = this.drawnLayerEntries().find((e) => e.id === entryId);
    if (!current) return;
    this.drawnLayerEntries.update((arr) =>
      arr.map((e) =>
        e.id === entryId
          ? { ...e, name: e.defaultLabel, rowState: 'reset' as const }
          : e,
      ),
    );
    this.syncLayerNameOnFeatures(entryId, current.defaultLabel);
  }

  onLayerExport(entryId: string): void {
    const entry = this.drawnLayerEntries().find((e) => e.id === entryId);
    if (!entry) return;
    this.layerExportEntryId.set(entryId);
    this.layerExportFileName = entry.name.trim() || entry.defaultLabel;
    this.layerExportDialogOpen.set(true);
  }

  closeLayerExportDialog(): void {
    this.layerExportDialogOpen.set(false);
    this.layerExportEntryId.set(null);
    this.layerExportFileName = '';
  }

  onLayerExportNameInput(ev: Event): void {
    this.layerExportFileName = (ev.target as HTMLInputElement).value;
  }

  canConfirmLayerExport(): boolean {
    return this.layerExportFileName.trim().length > 0;
  }

  confirmLayerExport(): void {
    const entryId = this.layerExportEntryId();
    if (!entryId || !this.canConfirmLayerExport()) return;

    const source = this.vectorSourceForLayerEntry(entryId);
    if (!source) {
      this.notifier?.error('No features found for this layer.');
      this.closeLayerExportDialog();
      return;
    }

    const features = source
      .getFeatures()
      .filter((f) => f.get('cvEntryId') === entryId);
    if (features.length === 0) {
      this.notifier?.error('No features found for this layer.');
      this.closeLayerExportDialog();
      return;
    }

    const name = this.layerExportFileName
      .trim()
      .replace(/[/\\?%*:|"<>]/g, '-');
    const json = exportLayerFeaturesAsGeoJson(source, entryId);
    const anchor = this.layerExportAnchor?.nativeElement;
    if (!anchor) {
      this.closeLayerExportDialog();
      return;
    }

    const blob = new Blob([json], { type: 'application/geo+json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    anchor.href = url;
    anchor.download = `${name}.geojson`;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 100);
    this.closeLayerExportDialog();
  }

  onLayerZoomToBounds(entryId: string): void {
    if (!this.map) return;
    const extent = this.extentForLayerEntry(entryId);
    if (!extent || extent.some((n) => !Number.isFinite(n))) {
      this.notifier?.error('Extent not available for this layer.');
      return;
    }
    this.map.getView().fit(extent, {
      padding: [100, 100, 100, 100],
      maxZoom: 14,
      duration: 500,
    });
  }

  onLayerAbout(entryId: string): void {
    const entry = this.drawnLayerEntries().find((e) => e.id === entryId);
    if (!entry?.datasetId) return;
    const url = this.datasetDetailUrl(entry.datasetId);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private vectorSourceForLayerEntry(entryId: string): VectorSource | null {
    const plotted = this.plottedDatasetLayers.get(entryId);
    if (plotted?.source) return plotted.source;
    const entry = this.drawnLayerEntries().find((e) => e.id === entryId);
    if (entry?.kind === 'draw') return this.userDrawSource;
    return null;
  }

  /** Visible vector layers for toolbar export (ui-canvas export parity). */
  readonly getCanvasExportLayers = (): CanvasExportLayerDescriptor[] => {
    const layers: CanvasExportLayerDescriptor[] = [];
    for (const entry of this.drawnLayerEntries()) {
      if (!entry.visible) continue;
      const source = this.vectorSourceForLayerEntry(entry.id);
      if (!source) continue;
      const label = entry.name.trim() || entry.defaultLabel;
      layers.push({ name: label, entryId: entry.id, source });
    }
    return layers;
  };

  private extentForLayerEntry(entryId: string): number[] | null {
    const source = this.vectorSourceForLayerEntry(entryId);
    if (!source) return null;
    const features = source
      .getFeatures()
      .filter((f) => f.get('cvEntryId') === entryId);
    if (features.length === 0) return null;
    const extent = createEmptyExtent();
    for (const f of features) {
      const geom = f.getGeometry();
      if (geom) extendExtent(extent, geom.getExtent());
    }
    return extent[0] === Infinity ? null : extent;
  }

  onClearAllLayers(): void {
    if (this.drawnLayerEntries().length === 0) return;

    this.removeAllPlottedDatasetLayers();
    this.userDrawSource.clear();
    this.userFeaturesLayer?.changed();
    this.drawnLayerEntries.set([]);
    this.shapeOrdinal = { circle: 0, rectangle: 0, polygon: 0, marker: 0 };
    this.datasetLayerOrdinal = 0;

    if (this.swipeActive()) {
      this.deactivateSwipe();
    }
    if (this.isSidebarOpen()) {
      this.closeSidebar();
    }
  }

  onDeleteDrawnLayer(entryId: string): void {
    const entry = this.drawnLayerEntries().find((e) => e.id === entryId);
    if (entry?.kind === 'dataset') {
      const plotted = this.plottedDatasetLayers.get(entryId);
      if (plotted && this.map) {
        this.map.removeLayer(plotted.olLayer);
        this.plottedDatasetLayers.delete(entryId);
      }
    } else {
      const feat = this.userDrawSource
        .getFeatures()
        .find((f) => f.get('cvEntryId') === entryId);
      if (feat) {
        this.userDrawSource.removeFeature(feat);
      }
      this.userFeaturesLayer.changed();
    }
    this.drawnLayerEntries.update((arr) => arr.filter((e) => e.id !== entryId));
    if (this.sidebarEntryId() === entryId) {
      this.closeSidebar();
    }
  }

  private syncLayerNameOnFeatures(entryId: string, name: string): void {
    const plotted = this.plottedDatasetLayers.get(entryId);
    if (plotted) {
      if (plotted.source) {
        for (const f of plotted.source.getFeatures()) {
          f.set('cvLayerName', name);
        }
      }
      plotted.olLayer.changed();
      return;
    }
    for (const f of this.userDrawSource.getFeatures()) {
      if (f.get('cvEntryId') === entryId) {
        f.set('cvLayerName', name);
        break;
      }
    }
  }

  onStartEditDrawnLayerName(entryId: string): void {
    this.drawnLayerEntries.update((arr) =>
      arr.map((e) =>
        e.id === entryId ? { ...e, rowState: 'editing' as const } : e,
      ),
    );
  }

  private registerDrawnShapeLayer(feature: Feature, mode: DrawShapeMode): void {
    if (mode !== 'circle' && mode !== 'rectangle' && mode !== 'polygon') return;
    const shape = mode as UserDrawShapeKind;
    this.registerUserLayer(feature, shape);
  }

  /**
   * Registers a feature in the layers panel (rename, visibility, swipe, export).
   */
  private registerUserLayer(
    feature: Feature,
    shape: UserDrawShapeKind,
    rowState: UserDrawLayerRowState = 'editing',
  ): void {
    const id = crypto.randomUUID();
    feature.set('cvEntryId', id);
    feature.set('cvHidden', false);
    this.shapeOrdinal[shape] += 1;
    const shapeWord = shape.charAt(0).toUpperCase() + shape.slice(1);
    const defaultLabel = `${shapeWord} ${this.shapeOrdinal[shape]}`;
    feature.set('cvLayerName', defaultLabel);
    const layerColor =
      shape === 'marker' ? getCanvasColor(CANVAS_COLOR_VARS.markerDefault) : getRandomLayerColor();
    feature.set('cvLayerColor', layerColor);
    feature.set('cvMarkerIconId', 0);
    this.drawnLayerEntries.update((arr) => {
      const next = [
        ...arr,
        this.buildDrawLayerEntry(id, shape, defaultLabel, rowState),
      ];
      this.applyLayerStackOrder(next);
      return next;
    });
    this.closeSidebar();
    this.isLayerPanelOpen.set(true);
  }

  /**
   * Called when the import tool has parsed a GeoJSON file.
   * For 'replace' mode: clears existing layer entries and shape ordinals first.
   * Registers each drawable feature in the layers panel so they behave
   * identically to user-drawn shapes (toggleable, renameable, swipe-ready).
   */
  onFeaturesImported({ features, mode }: ImportedFeaturesPayload): void {
    if (mode === 'replace') {
      this.userDrawSource.clear();
      this.drawnLayerEntries.update((arr) => arr.filter((e) => e.kind === 'dataset'));
      this.shapeOrdinal = { circle: 0, rectangle: 0, polygon: 0, marker: 0 };
      if (this.swipeActive()) {
        this.deactivateSwipe();
      }
    }

    for (const f of features) {
      const geom = f.getGeometry();
      let shape: UserDrawShapeKind | null = null;
      if (geom instanceof Point) {
        shape = 'marker';
      } else if (
        geom instanceof Polygon ||
        geom instanceof MultiPolygon ||
        geom instanceof Circle
      ) {
        shape = 'polygon';
      }
      if (!shape) continue;

      const id = crypto.randomUUID();
      f.set('cvEntryId', id);
      f.set('cvHidden', false);
      this.shapeOrdinal[shape] += 1;
      const shapeWord = shape.charAt(0).toUpperCase() + shape.slice(1);
      const defaultLabel = `${shapeWord} ${this.shapeOrdinal[shape]}`;
      f.set('cvLayerName', defaultLabel);
      const layerColor =
        shape === 'marker' ? getCanvasColor(CANVAS_COLOR_VARS.markerDefault) : getRandomLayerColor();
      f.set('cvLayerColor', layerColor);
      f.set('cvMarkerIconId', 0);
      this.drawnLayerEntries.update((arr) => [
        ...arr,
        this.buildDrawLayerEntry(id, shape, defaultLabel, 'saved'),
      ]);
    }
    if (features.some((f) => f.get('cvEntryId'))) {
      this.closeSidebar();
      this.isLayerPanelOpen.set(true);
      this.applyLayerStackOrder(this.drawnLayerEntries());
    }
    this.userFeaturesLayer.changed();
  }

  openDatasetsModal(): void {
    this.cancelBboxSearch();
    this.isDatasetsModalOpen.set(true);
  }

  onCanvasSearch(mode: CanvasSearchMode): void {
    if (mode === 'global') {
      this.openDatasetsModal();
      return;
    }
    if (mode === 'bbox') {
      this.isDatasetsModalOpen.set(false);
      this.beginBboxSearch();
    }
  }

  onDatasetsBboxSearch(): void {
    this.isDatasetsModalOpen.set(false);
    this.beginBboxSearch();
  }

  onDatasetsBboxFilter(dataset: CanvasDataset): void {
    this.isDatasetsModalOpen.set(false);
    this.beginBboxFilter(dataset);
  }

  onDatasetsGetAll(dataset: CanvasDataset): void {
    this.isDatasetsModalOpen.set(false);
    void this.loadDatasetOnMap(dataset, {});
  }

  beginBboxSearch(): void {
    this.cancelBboxSearch();
    this.bboxMode = 'search';
    this.bboxFilterDataset = null;
    this.bboxSearchActive.set(true);
    this.activeToolGroup.set('select');
    this.showBboxNotice('Draw a rectangle to search datasets in this area.');
    this.startBboxDrawInteraction();
  }

  beginBboxFilter(dataset: CanvasDataset): void {
    if (!dataset.resourceServerUrl) {
      this.notifier?.error(
        'This dataset has no OGC Features resource server URL.',
      );
      return;
    }

    this.cancelBboxSearch();
    this.bboxMode = 'filter';
    this.bboxFilterDataset = dataset;
    this.bboxSearchActive.set(true);
    this.activeToolGroup.set('select');
    this.showBboxNotice(`Draw a rectangle to filter "${dataset.name}".`);
    this.startBboxDrawInteraction();
  }

  private startBboxDrawInteraction(): void {
    if (!this.map) return;
    this.clearInteractions({ keepBboxSearch: true });
    this.bboxSearchActive.set(true);

    const draw = new Draw({
      source: this.bboxDrawSource,
      type: 'Circle',
      geometryFunction: createBox(),
      stopClick: true,
      maxPoints: 2,
    });

    draw.on('drawstart', () => {
      this.bboxDrawSource.clear();
      this.bboxSearchSource.clear();
    });

    draw.on('drawend', (e) => {
      this.map?.removeInteraction(draw);
      if (this.activeDraw === draw) {
        this.activeDraw = null;
      }
      this.bboxSearchActive.set(false);
      void this.onBboxDrawComplete(e.feature as Feature);
    });

    this.map.addInteraction(draw);
    this.activeDraw = draw;
  }

  private async onBboxDrawComplete(feature: Feature): Promise<void> {
    const bbox = geometryToOgcBbox(feature.getGeometry() ?? undefined);
    if (!bbox || !this.bboxMode) {
      this.notifier?.error('Could not read the drawn bounding box.');
      return;
    }

    this.bboxSearchLoading.set(true);
    this.showBboxNotice('Loading features in the selected area…');

    try {
      if (this.bboxMode === 'search') {
        await this.runBboxSearch(bbox);
      } else if (this.bboxMode === 'filter' && this.bboxFilterDataset) {
        await this.runBboxFilter(bbox, this.bboxFilterDataset);
      }
    } catch (err: unknown) {
      const msg =
        (err as { error?: { detail?: string }; message?: string })?.error
          ?.detail ??
        (err as Error)?.message ??
        'Failed to fetch OGC features for this area.';
      this.showBboxNotice(
        typeof msg === 'string' ? msg : 'BBox request failed.',
      );
      this.notifier?.error(
        typeof msg === 'string' ? msg : 'BBox request failed.',
      );
    } finally {
      this.bboxSearchLoading.set(false);
    }
  }

  private async runBboxSearch(
    bbox: ReturnType<typeof geometryToOgcBbox>,
  ): Promise<void> {
    if (!bbox) return;

    const rawHits = await this.ogcFeaturesApi.searchResourcesByBbox(bbox);
    const publishedIds = await fetchPublishedDatasetIds(
      this.catalogueApi,
      rawHits.map((hit) => hit.resourceId),
    );
    const hits = rawHits.filter((hit) => publishedIds.has(hit.resourceId));
    this.bboxSearchHits.set(hits);
    this.displayBboxSearchHits(hits);

    const count = hits.length;
    this.isBboxResultsModalOpen.set(false);
    this.bboxSearchSummaryVisible.set(count > 0);

    if (count === 0) {
      this.showBboxNotice('No resources found in the selected area.');
      this.notifier?.success(
        'BBox search complete — no resources in this area.',
      );
    } else {
      this.fitMapToOgcResults();
    }
  }

  private async runBboxFilter(
    bbox: NonNullable<ReturnType<typeof geometryToOgcBbox>>,
    dataset: CanvasDataset,
  ): Promise<void> {
    const queryParams = buildDatasetQueryParams(bbox);
    await this.loadDatasetOnMap(dataset, queryParams, {
      fitToDrawnBbox: true,
      successMessage: 'BBox filter completed successfully.',
      emptyMessage: 'No data available in the selected area.',
    });
  }

  private displayBboxSearchHits(hits: CanvasBboxSearchHit[]): void {
    this.bboxSearchSource.clear();
    if (hits.length === 0) return;

    const features = hits.map((hit) => {
      const feature = new OlFeature({
        geometry: new Point(hit.coordinate),
      });
      feature.set('cvOgcFeature', true);
      feature.set('cvBboxSearchHit', hit);
      feature.set('cvLayerName', hit.label);
      return feature;
    });

    this.bboxSearchSource.addFeatures(features);
    this.syncBboxMarkerSelectionStyles();
    this.bboxSearchLayer.changed();
  }

  /**
   * Plots dataset features on a dedicated map layer and registers a layers-panel row
   * (ui-canvas `createNewUgixLayer` + `addCanvasLayer`).
   */
  private addPlottedDatasetLayer(
    dataset: CanvasDataset,
    rawFeatures: CanvasOgcFeature[],
  ): string | null {
    if (!this.map || rawFeatures.length === 0) return null;

    const format = new GeoJSON();
    const parsed = format.readFeatures(
      { type: 'FeatureCollection', features: rawFeatures },
      { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:4326' },
    );

    const layerId = crypto.randomUUID();
    const source = new VectorSource();
    const layerColor = getRandomLayerColor();
    const layerName = dataset.name.trim() || 'Dataset';
    let hasPoint = false;
    let hasArea = false;

    for (const f of parsed) {
      f.set('cvOgcFeature', true);
      f.set('cvEntryId', layerId);
      f.set('cvLayerName', layerName);
      f.set('cvLayerColor', layerColor);
      f.set('cvMarkerIconId', 0);
      const g = f.getGeometry();
      if (g instanceof Point) hasPoint = true;
      else if (g) hasArea = true;
    }
    source.addFeatures(parsed);

    const olLayer = new VectorLayer({
      source,
      zIndex: MapsHomeComponent.PANEL_LAYER_Z_BASE,
      style: createDatasetVectorStyleFn(
        this.iconsBaseUrl,
        layerColor,
        0,
      ),
    });

    this.map.addLayer(olLayer);
    this.plottedDatasetLayers.set(layerId, { source, olLayer });

    this.datasetLayerOrdinal += 1;
    const defaultLabel =
      this.datasetLayerOrdinal > 1
        ? `${layerName} (${this.datasetLayerOrdinal})`
        : layerName;

    this.drawnLayerEntries.update((arr) => {
      const next = [
        ...arr,
        this.buildDatasetLayerEntry(
          layerId,
          dataset.id,
          defaultLabel,
          this.datasetStyleControls({ hasPoint, hasArea, isImage: false }),
          layerColor,
        ),
      ];
      this.applyLayerStackOrder(next);
      return next;
    });
    this.closeSidebar();
    this.isLayerPanelOpen.set(true);
    return layerId;
  }

  private fitMapToOgcResults(): void {
    if (!this.map) return;
    const extent = createEmptyExtent();
    const bboxExtent = this.bboxDrawSource.getExtent();
    const featureExtent = this.bboxSearchSource.getExtent();
    if (bboxExtent && bboxExtent.every((n) => Number.isFinite(n))) {
      extendExtent(extent, bboxExtent);
    }
    if (featureExtent && featureExtent.every((n) => Number.isFinite(n))) {
      extendExtent(extent, featureExtent);
    }
    if (extent[0] === Infinity) return;
    this.map
      .getView()
      .fit(extent, { padding: [48, 48, 48, 48], maxZoom: 14, duration: 400 });
  }

  openBboxResultsList(): void {
    if (this.bboxSearchHits().length > 0) {
      this.isBboxResultsModalOpen.set(true);
    }
  }

  closeBboxResultsModal(): void {
    this.isBboxResultsModalOpen.set(false);
  }

  onToggleBboxResourceSelection(resourceId: string): void {
    this.selectedBboxResourceIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(resourceId)) {
        next.delete(resourceId);
      } else {
        next.add(resourceId);
      }
      return next;
    });
    this.syncBboxMarkerSelectionStyles();
  }

  onBboxBannerBackToDatasets(): void {
    this.bboxSearchSummaryVisible.set(false);
    this.isBboxResultsModalOpen.set(false);
    this.openDatasetsModal();
  }

  onBboxResultStartFilter(dataset: CanvasDataset): void {
    this.closeBboxResultsModal();
    this.bboxSearchSummaryVisible.set(false);
    this.beginBboxFilter(dataset);
  }

  onBboxResultStartGetAll(dataset: CanvasDataset): void {
    this.closeBboxResultsModal();
    this.bboxSearchSummaryVisible.set(false);
    void this.loadDatasetOnMap(dataset, {});
  }

  onDatasetsAttrFilter(dataset: CanvasDataset): void {
    this.isDatasetsModalOpen.set(false);
    this.attrFilterDataset.set(dataset);
    this.isAttrFilterModalOpen.set(true);
  }

  onBboxResultAttrFilter(dataset: CanvasDataset): void {
    this.closeBboxResultsModal();
    this.bboxSearchSummaryVisible.set(false);
    this.attrFilterDataset.set(dataset);
    this.isAttrFilterModalOpen.set(true);
  }

  onDatasetsStacList(dataset: CanvasDataset): void {
    this.isDatasetsModalOpen.set(false);
    this.openStacList(dataset);
  }

  onBboxResultStacList(dataset: CanvasDataset): void {
    this.closeBboxResultsModal();
    this.bboxSearchSummaryVisible.set(false);
    this.openStacList(dataset);
  }

  onDatasetsLoadTiles(dataset: CanvasDataset): void {
    this.isDatasetsModalOpen.set(false);
    void this.loadTilesOnMap(dataset);
  }

  onBboxResultLoadTiles(dataset: CanvasDataset): void {
    this.closeBboxResultsModal();
    this.bboxSearchSummaryVisible.set(false);
    void this.loadTilesOnMap(dataset);
  }

  private openStacList(dataset: CanvasDataset): void {
    this.stacDataset.set(dataset);
    this.isStacModalOpen.set(true);
  }

  closeStacModal(): void {
    this.isStacModalOpen.set(false);
    this.stacDataset.set(null);
  }

  onStacPlot(payload: CanvasStacPlotPayload): void {
    const dataset = this.stacDataset();
    if (!dataset) {
      this.notifier?.error('STAC dataset context was lost. Open the list again.');
      return;
    }
    if (!this.map) {
      this.notifier?.error('Map is not ready yet. Try again in a moment.');
      return;
    }
    this.closeStacModal();

    const layerId = crypto.randomUUID();
    let bundle: PlottedOlLayerBundle;
    let settled = false;

    const finish = (useImage: boolean): void => {
      if (settled) return;
      settled = true;
      bundle = useImage
        ? createStacImageLayer(payload.imageUrl!, payload.bbox, layerId)
        : createBboxBoundaryLayer(payload.bbox, layerId);
      this.finishGenericDatasetLayer(dataset, bundle, payload.bbox);
      this.notifier?.success(
        useImage
          ? `Plotted STAC image for "${dataset.name}".`
          : `Plotted STAC footprint for "${dataset.name}".`,
      );
    };

    const stacColor = getRandomLayerColor();

    if (payload.imageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      const timeout = window.setTimeout(() => {
        if (settled) return;
        bundle = createBboxBoundaryLayer(payload.bbox, layerId, stacColor);
        finish(false);
      }, 15_000);
      img.onload = () => {
        window.clearTimeout(timeout);
        finish(true);
      };
      img.onerror = () => {
        window.clearTimeout(timeout);
        bundle = createBboxBoundaryLayer(payload.bbox, layerId, stacColor);
        finish(false);
      };
      img.src = payload.imageUrl;
      return;
    }

    bundle = createBboxBoundaryLayer(payload.bbox, layerId, stacColor);
    this.finishGenericDatasetLayer(dataset, bundle, payload.bbox);
    this.notifier?.success(`Plotted STAC footprint for "${dataset.name}".`);
  }

  private async loadTilesOnMap(dataset: CanvasDataset): Promise<void> {
    if (!(await this.ensureDatasetLoadAccess(dataset))) {
      return;
    }

    const serverUrl =
      dataset.tileServerUrl?.trim() || dataset.resourceServerUrl?.trim();
    if (!serverUrl) {
      this.notifier?.error('This dataset has no tile resource server URL.');
      return;
    }

    this.datasetLoadLoading.set(true);
    this.showBboxNotice(`Loading tiles for "${dataset.name}"…`);

    try {
      const token = await this.keycloak.getToken();
      if (!token) {
        throw new Error('Unable to obtain access token.');
      }

      await this.ogcFeaturesApi.getTileLayerUrl(dataset.id, serverUrl);
      const layerId = crypto.randomUUID();
      const isRaster =
        dataset.ogcResourceApis.includes('RASTER_TILES') ||
        dataset.resourceApiType === 'RASTER_TILES';
      const tilesUrl = `${serverUrl.replace(/\/+$/, '')}/collections/${encodeURIComponent(dataset.id)}/map/tiles/WorldCRS84Quad`;

      const bundle = isRaster
        ? createRasterTileLayerFromUrl(tilesUrl, token, layerId)
        : createVectorTileLayerFromUrl(tilesUrl, token, layerId);

      this.finishGenericDatasetLayer(dataset, bundle);
      this.notifier?.success('Tile layer loaded successfully.');
    } catch (err: unknown) {
      const msg =
        (err as Error)?.message ?? 'Failed to load tile layer.';
      this.showBboxNotice(msg);
      this.notifier?.error(msg);
    } finally {
      this.datasetLoadLoading.set(false);
    }
  }

  private finishGenericDatasetLayer(
    dataset: CanvasDataset,
    bundle: PlottedOlLayerBundle,
    extent?: StacBbox,
  ): void {
    if (!this.map) return;

    const layerId = bundle.layerId;
    const layerColor = getRandomLayerColor();
    const isImageLayer = bundle.olLayer instanceof ImageLayer;
    if (bundle.source) {
      for (const f of bundle.source.getFeatures()) {
        f.set('cvLayerColor', layerColor);
        f.set('cvMarkerIconId', 0);
      }
      const vectorLayer = bundle.olLayer as VectorLayer<VectorSource>;
      vectorLayer.setStyle(
        createDatasetVectorStyleFn(this.iconsBaseUrl, layerColor, 0),
      );
    }
    if (isImageLayer) {
      bundle.olLayer.setOpacity(1);
    }
    this.map.addLayer(bundle.olLayer);
    this.plottedDatasetLayers.set(layerId, {
      source: bundle.source,
      olLayer: bundle.olLayer,
      isImageLayer,
    });

    this.datasetLayerOrdinal += 1;
    const layerName = dataset.name.trim() || 'Dataset';
    const defaultLabel =
      this.datasetLayerOrdinal > 1
        ? `${layerName} (${this.datasetLayerOrdinal})`
        : layerName;

    this.drawnLayerEntries.update((arr) => {
      const next = [
        ...arr,
        this.buildDatasetLayerEntry(
          layerId,
          dataset.id,
          defaultLabel,
          this.datasetStyleControls({
            hasPoint: false,
            hasArea: Boolean(bundle.source),
            isImage: isImageLayer,
          }),
          layerColor,
        ),
      ];
      this.applyLayerStackOrder(next);
      return next;
    });
    this.closeSidebar();
    this.isLayerPanelOpen.set(true);

    if (extent) {
      fitMapToExtent(this.map, extent);
    }
  }

  closeAttrFilterModal(): void {
    this.isAttrFilterModalOpen.set(false);
    this.attrFilterDataset.set(null);
  }

  onAttrFilterApply(filters: Record<string, string>): void {
    const dataset = this.attrFilterDataset();
    if (!dataset) return;
    this.isAttrFilterModalOpen.set(false);
    this.attrFilterDataset.set(null);
    void this.loadDatasetOnMap(dataset, filters, {
      successMessage: 'Attribute filter applied successfully.',
      emptyMessage: 'No features match the selected attribute filters.',
    });
  }

  private async ensureDatasetLoadAccess(
    dataset: CanvasDataset,
  ): Promise<boolean> {
    const policy = dataset.accessPolicy.trim().toUpperCase();
    if (policy === 'OPEN') {
      return true;
    }
    const isLoggedIn = await this.keycloak.isLoggedIn();
    if (!isLoggedIn) {
      this.notifier?.error('Please sign in to load this dataset.');
      await this.keycloak.login({ redirectUri: window.location.href });
      return false;
    }
    return true;
  }

  private async loadDatasetOnMap(
    dataset: CanvasDataset,
    queryParams: DatasetQueryParams,
    options?: {
      fitToDrawnBbox?: boolean;
      successMessage?: string;
      emptyMessage?: string;
    },
  ): Promise<void> {
    if (!(await this.ensureDatasetLoadAccess(dataset))) {
      return;
    }

    this.datasetLoadLoading.set(true);
    this.showBboxNotice(`Loading "${dataset.name}"…`);

    try {
      const { featureCount, features } =
        await this.datasetLoadService.fetchCollectionFeatures(
          dataset,
          queryParams,
        );

      if (featureCount === 0) {
        const emptyMsg =
          options?.emptyMessage ?? 'No data available for this dataset.';
        this.showBboxNotice(emptyMsg);
        this.notifier?.error(emptyMsg);
        return;
      }

      const layerId = this.addPlottedDatasetLayer(dataset, features);
      this.showBboxNotice(
        `${featureCount} feature${featureCount === 1 ? '' : 's'} loaded for ${dataset.name}.`,
      );
      this.notifier?.success(
        options?.successMessage ?? 'Dataset loaded successfully.',
      );

      if (options?.fitToDrawnBbox) {
        this.fitMapToOgcResults();
      } else if (layerId) {
        this.fitMapToPlottedLayerExtent(layerId);
      }
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === 'AbortError') {
        return;
      }
      const msg =
        (err as { error?: { detail?: string }; message?: string })?.error
          ?.detail ??
        (err as Error)?.message ??
        'Failed to load dataset features.';
      this.showBboxNotice(
        typeof msg === 'string' ? msg : 'Failed to load dataset features.',
      );
      this.notifier?.error(
        typeof msg === 'string' ? msg : 'Failed to load dataset features.',
      );
    } finally {
      this.datasetLoadLoading.set(false);
    }
  }

  private fitMapToPlottedLayerExtent(layerId: string): void {
    if (!this.map) return;
    const plotted = this.plottedDatasetLayers.get(layerId);
    if (!plotted?.source) return;
    const extent = plotted.source.getExtent();
    if (!extent || extent.some((n) => !Number.isFinite(n))) return;
    fitMapToExtent(this.map, extent);
  }

  private syncBboxMarkerSelectionStyles(): void {
    const selected = this.selectedBboxResourceIds();
    for (const feature of this.bboxSearchSource.getFeatures()) {
      const hit = feature.get('cvBboxSearchHit') as
        | CanvasBboxSearchHit
        | undefined;
      feature.set('cvBboxSelected', hit ? selected.has(hit.resourceId) : false);
    }
    this.bboxSearchLayer?.changed();
  }

  cancelBboxSearch(): void {
    this.datasetLoadService.abortLoad();
    this.bboxSearchActive.set(false);
    this.bboxSearchLoading.set(false);
    this.bboxMode = null;
    this.bboxFilterDataset = null;
    this.isBboxResultsModalOpen.set(false);
    this.bboxSearchSummaryVisible.set(false);
    this.bboxSearchHits.set([]);
    this.selectedBboxResourceIds.set(new Set());
    if (this.map && this.activeDraw) {
      this.map.removeInteraction(this.activeDraw);
      this.activeDraw = null;
    }
    this.bboxDrawSource.clear();
    this.bboxSearchSource.clear();
    this.bboxSearchLayer?.changed();
  }

  private removeAllPlottedDatasetLayers(): void {
    if (!this.map) return;
    for (const { olLayer } of this.plottedDatasetLayers.values()) {
      this.map.removeLayer(olLayer);
    }
    this.plottedDatasetLayers.clear();
    this.drawnLayerEntries.update((arr) =>
      arr.filter((e) => e.kind !== 'dataset'),
    );
  }

  private showBboxNotice(message: string): void {
    if (this.bboxNoticeTimer) {
      clearTimeout(this.bboxNoticeTimer);
      this.bboxNoticeTimer = null;
    }
    this.bboxSearchNotice.set(message);
    this.bboxNoticeTimer = setTimeout(() => {
      this.bboxSearchNotice.set(null);
      this.bboxNoticeTimer = null;
    }, 6000);
  }

  /** Called by the toolbar swipe button — opens setup or closes if already active. */
  openSwipeDialog(): void {
    if (this.swipeActive()) {
      this.deactivateSwipe();
      return;
    }
    const entries = this.drawnLayerEntries();
    if (entries.length < 1) {
      this.showSwipeEmptyNotice();
      return;
    }
    // Default: first layer(s) on left, remaining on right.
    this.swipeLeftIds.set([entries[0].id]);
    this.swipeRightIds.set(
      entries.length > 1 ? entries.slice(1).map((e) => e.id) : [entries[0].id],
    );
    this.swipeDialogOpen.set(true);
  }

  toggleSwipeLeft(id: string): void {
    this.swipeLeftIds.update((ids) => {
      const next = ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id];
      return next;
    });
    this.swipeRightIds.update((ids) =>
      ids.includes(id) ? ids.filter((i) => i !== id) : ids,
    );
  }

  toggleSwipeRight(id: string): void {
    this.swipeRightIds.update((ids) => {
      const next = ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id];
      return next;
    });
    this.swipeLeftIds.update((ids) =>
      ids.includes(id) ? ids.filter((i) => i !== id) : ids,
    );
  }

  swipeLayerNames(ids: string[]): string {
    return (
      ids
        .map(
          (id) =>
            this.drawnLayerEntries().find((e) => e.id === id)?.name ?? '—',
        )
        .join(', ') || '—'
    );
  }

  applySwipe(): void {
    const leftIds = this.swipeLeftIds();
    const rightIds = this.swipeRightIds();
    if ((!leftIds.length && !rightIds.length) || !this.map) return;

    this.swipeDialogOpen.set(false);
    this.removeSwipeLayers();

    const leftDrawIds: string[] = [];
    const rightDrawIds: string[] = [];
    const middleDrawIds: string[] = [];

    for (const entry of this.drawnLayerEntries()) {
      if (!entry.visible) continue;
      const side = this.resolveSwipeSide(entry.id, leftIds, rightIds);

      if (entry.kind === 'dataset') {
        const plotted = this.plottedDatasetLayers.get(entry.id);
        if (!plotted?.olLayer || side === 'middle') continue;
        this.attachSwipeClipToLayer(plotted.olLayer, side);
        continue;
      }

      if (side === 'left') leftDrawIds.push(entry.id);
      else if (side === 'right') rightDrawIds.push(entry.id);
      else middleDrawIds.push(entry.id);
    }

    this.swipeMiddleDrawIds = middleDrawIds;
    this.swipeActive.set(true);
    this.userFeaturesLayer.setVisible(true);
    this.userFeaturesLayer.setStyle((feat) =>
      this.styleUserDrawFeature(feat as Feature),
    );

    if (leftDrawIds.length) {
      this.swipeLeftVLayer = new VectorLayer({
        source: this.userDrawSource,
        zIndex: 82,
        style: this.createSwipeDrawStyle(leftDrawIds),
      });
      this.attachSwipeClipToLayer(this.swipeLeftVLayer, 'left');
      this.map.addLayer(this.swipeLeftVLayer);
    }

    if (rightDrawIds.length) {
      this.swipeRightVLayer = new VectorLayer({
        source: this.userDrawSource,
        zIndex: 82,
        style: this.createSwipeDrawStyle(rightDrawIds),
      });
      this.attachSwipeClipToLayer(this.swipeRightVLayer, 'right');
      this.map.addLayer(this.swipeRightVLayer);
    }

    this.swipePosition.set(0.5);
    this.map.render();
  }

  /** ui-canvas LayerCard: left-only, right-only, or visible on both sides. */
  private resolveSwipeSide(
    entryId: string,
    leftIds: string[],
    rightIds: string[],
  ): 'left' | 'right' | 'middle' {
    const onLeft = leftIds.includes(entryId);
    const onRight = rightIds.includes(entryId);
    if (onLeft && !onRight) return 'left';
    if (onRight && !onLeft) return 'right';
    return 'middle';
  }

  private styleUserDrawFeature(
    feature: Feature,
    options?: { ignoreSwipeFilter?: boolean },
  ): Style | undefined {
    if (feature.get('cvHidden')) return undefined;

    if (!options?.ignoreSwipeFilter && this.swipeActive()) {
      const entryId = feature.get('cvEntryId') as string | undefined;
      if (entryId && !this.swipeMiddleDrawIds.includes(entryId)) {
        return undefined;
      }
    }

    const g = feature.getGeometry();
    if (!g) return undefined;
    const color =
      (feature.get('cvLayerColor') as string | undefined) ??
      getCanvasColor(CANVAS_COLOR_VARS.markerFallbackBlue);
    const markerId =
      (feature.get('cvMarkerIconId') as number | undefined) ?? 0;
    if (g instanceof Point) {
      return markerIconStyle(this.iconsBaseUrl, markerId);
    }
    return vectorColorStyle(color, feature);
  }

  private createSwipeDrawStyle(entryIds: string[]): StyleLike {
    return (feat) => {
      const feature = feat as Feature;
      const entryId = feature.get('cvEntryId') as string | undefined;
      if (!entryId || !entryIds.includes(entryId) || feature.get('cvHidden')) {
        return undefined;
      }
      return this.styleUserDrawFeature(feature, { ignoreSwipeFilter: true });
    };
  }

  private createSwipeClipPre(
    side: 'left' | 'right',
  ): (event: RenderEvent) => void {
    return (event) => {
      const ctx = event.context as CanvasRenderingContext2D;
      const map = this.map;
      if (!map) return;
      const size = map.getSize();
      if (!size || !ctx) return;

      const width = size[0] * this.swipePosition();
      let tl: number[];
      let tr: number[];
      let bl: number[];
      let br: number[];

      if (side === 'left') {
        tl = getRenderPixel(event, [0, 0]);
        tr = getRenderPixel(event, [width, 0]);
        bl = getRenderPixel(event, [0, size[1]]);
        br = getRenderPixel(event, [width, size[1]]);
      } else {
        tl = getRenderPixel(event, [width, 0]);
        tr = getRenderPixel(event, [size[0], 0]);
        bl = getRenderPixel(event, [width, size[1]]);
        br = getRenderPixel(event, size);
      }

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(tl[0], tl[1]);
      ctx.lineTo(bl[0], bl[1]);
      ctx.lineTo(br[0], br[1]);
      ctx.lineTo(tr[0], tr[1]);
      ctx.closePath();
      ctx.clip();
    };
  }

  private createSwipeClipPost(): (event: RenderEvent) => void {
    return (event) => {
      (event.context as CanvasRenderingContext2D).restore();
    };
  }

  private attachSwipeClipToLayer(
    layer: BaseLayer,
    side: 'left' | 'right',
  ): void {
    const olLayer = layer as Layer;
    const prerender = this.createSwipeClipPre(side);
    const postrender = this.createSwipeClipPost();
    olLayer.on('prerender', prerender);
    olLayer.on('postrender', postrender);
    this.swipeClipBindings.set(olLayer, { prerender, postrender });
  }

  private detachAllSwipeClips(): void {
    for (const [layer, { prerender, postrender }] of this.swipeClipBindings) {
      layer.un('prerender', prerender);
      layer.un('postrender', postrender);
    }
    this.swipeClipBindings.clear();
  }

  private removeSwipeLayers(): void {
    this.detachAllSwipeClips();

    if (this.swipeLeftVLayer) {
      this.map?.removeLayer(this.swipeLeftVLayer);
      this.swipeLeftVLayer = null;
    }
    if (this.swipeRightVLayer) {
      this.map?.removeLayer(this.swipeRightVLayer);
      this.swipeRightVLayer = null;
    }
  }

  deactivateSwipe(): void {
    this.removeSwipeLayers();
    this.swipeMiddleDrawIds = [];
    this.userFeaturesLayer.setVisible(true);
    this.userFeaturesLayer.setStyle((feat) =>
      this.styleUserDrawFeature(feat as Feature),
    );
    this.swipeActive.set(false);
    this.swipeDialogOpen.set(false);
    this.map?.render();
  }

  onSwipeDragStart(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    const mapEl = this.mapContainerRef.nativeElement;
    const move = (ev: MouseEvent | TouchEvent) => {
      const clientX =
        ev instanceof TouchEvent ? ev.touches[0].clientX : ev.clientX;
      const rect = mapEl.getBoundingClientRect();
      this.swipePosition.set(
        Math.min(0.97, Math.max(0.03, (clientX - rect.left) / rect.width)),
      );
      this.map?.render();
    };
    const up = () => {
      window.removeEventListener('mousemove', move as EventListener);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move as EventListener);
      window.removeEventListener('touchend', up);
    };
    window.addEventListener('mousemove', move as EventListener);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move as EventListener, {
      passive: false,
    });
    window.addEventListener('touchend', up);
  }

  closeSwipeDialog(): void {
    this.deactivateSwipe();
  }

  private showSwipeEmptyNotice(): void {
    if (this.swipeEmptyNoticeTimer) {
      clearTimeout(this.swipeEmptyNoticeTimer);
      this.swipeEmptyNoticeTimer = null;
    }
    this.swipeEmptyNotice.set(
      'There are no layers to compare. Draw a shape, add a marker, or load a dataset first.',
    );
    this.swipeEmptyNoticeTimer = setTimeout(() => {
      this.swipeEmptyNotice.set(null);
      this.swipeEmptyNoticeTimer = null;
    }, 4500);
  }

  zoomIn(): void {
    if (!this.map) return;
    const view = this.map.getView();
    view.animate({ zoom: (view.getZoom() ?? 4) + 1, duration: 200 });
  }

  zoomOut(): void {
    if (!this.map) return;
    const view = this.map.getView();
    view.animate({ zoom: (view.getZoom() ?? 4) - 1, duration: 200 });
  }

  ngOnDestroy(): void {
    if (this.swipeEmptyNoticeTimer) {
      clearTimeout(this.swipeEmptyNoticeTimer);
      this.swipeEmptyNoticeTimer = null;
    }
    if (this.bboxNoticeTimer) {
      clearTimeout(this.bboxNoticeTimer);
      this.bboxNoticeTimer = null;
    }
    this.deactivateSwipe();
    this.cancelBboxSearch();
    this.removeAllPlottedDatasetLayers();
    this.clearInteractions();
    if (this.map) {
      this.map
        .getViewport()
        .removeEventListener('contextmenu', this.onMapContextMenu);
      this.map.getView().un('change:resolution', this.onViewResolutionChange);
      this.map.setTarget(undefined);
    }
    this.map = null;
  }
}
